import styled from '@emotion/styled';
import { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { SimCardStates } from '@racemap/sdk/schema/simCard';
import { Alert, Flex, Space, Typography } from 'antd';
import type { Immutable } from 'immer';
import { type FC, useState } from 'react';
import { useCurrentUser, useGetProductPrice } from '../../../lib/customHooks';
import { formatPrice } from '../../../misc/pricing';
import type { ManagedTracker } from '../../../store/trackers/trackers_reducers';
import { ExternalLink } from '../../BasicComponents';
import { Placeholder } from '../../BasicComponents/Placeholder';
import { IconExclamationTriangle } from '../../Icon';

const { Text, Link } = Typography;

interface Props {
  trackers: Immutable<Array<ManagedTracker>>;
}

export const SMSCommandReport: FC<Props> = ({ trackers }) => {
  const trackersWithActivatedSim = trackers.filter(isAnSMSReadyTracker);
  const trackersWithNoSimCard = trackers.filter((t) => t.simCard == null);
  const trackersWithNotActivatedSim = trackers.filter(
    (t) => t.simCard != null && t.simCard?.status !== SimCardStates.AKTIV,
  );

  return (
    <Wrapper>
      {trackersWithNotActivatedSim.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<IconExclamationTriangle />}
          message="No activated SIM-Card"
          description={
            <DescriptionWithSimCardList
              trackers={trackersWithNotActivatedSim}
              text1="Following tracker have no activated SIM-Card."
              text2={
                'Activate the SIM-Cards in the the SIM Management or contact the SIM-Cards-Owner.'
              }
            />
          }
        />
      )}
      {trackersWithNoSimCard.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<IconExclamationTriangle />}
          message="No SIM-Card"
          description={
            <DescriptionWithSimCardList
              trackers={trackersWithNoSimCard}
              text1="Following tracker have no SIM-Card managed by Racemap."
              text2={
                <>
                  Read more{' '}
                  <ExternalLink
                    link="https://racemap.com/gps-tracker#sim-subscription"
                    label="here"
                  />
                  to our M2M-SIM-Cards.
                </>
              }
            />
          }
        />
      )}
      <Alert
        type="info"
        showIcon
        message="Additional costs"
        description={<CostDescription trackers={trackersWithActivatedSim} />}
      />
    </Wrapper>
  );
};

interface DescriptionWItmSimCardListProps {
  trackers: Immutable<Array<ManagedTracker>>;
  text1: React.ReactNode;
  text2: React.ReactNode;
}

const DescriptionWithSimCardList: FC<DescriptionWItmSimCardListProps> = ({
  trackers,
  text1,
  text2,
}) => {
  const [expand, setExpand] = useState(false);
  const trackersReduced = trackers.slice(0, expand ? trackers.length : 3);

  return (
    <Space direction="vertical">
      <Text>{text1}</Text>
      <Text>
        <ul>
          {trackersReduced.map((t) => (
            <li key={t.id.toHexString()}>{t.trackerName || t.id.toHexString()}</li>
          ))}
          {!expand && trackers.length > 3 && '...'}
        </ul>
        {trackers.length > 3 && (
          <Link onClick={() => setExpand(!expand)}>{expand ? 'Less' : 'More'}</Link>
        )}
      </Text>
      <Text>{text2}</Text>
    </Space>
  );
};

const CostDescription: FC<Props> = ({ trackers }) => {
  const currentUser = useCurrentUser();
  if (currentUser == null) return null;

  return (
    <Flex vertical>
      <Text>
        <Price price={StripeProducts.SMS} /> per SMS
      </Text>
      <Text>
        <Text strong>Total:</Text> <Price quantity={trackers.length} price={StripeProducts.SMS} />{' '}
        for <Text strong>{trackers.length}</Text> SMS.
      </Text>
    </Flex>
  );
};

const Price: FC<{
  price: StripeProducts | undefined;
  quantity?: number;
}> = ({ price, quantity = 1 }) => {
  const getPriceOfProduct = useGetProductPrice();
  if (getPriceOfProduct == null) return <Placeholder />;
  if (price == null) return <Text>--</Text>;
  return <Text>{formatPrice(getPriceOfProduct(price, quantity))}</Text>;
};

const Wrapper = styled.div`
    margin-bottom: 24px;
    display: flex;
    flex-direction: column; 
    gap: 16px;
`;

export const isAnSMSReadyTracker = (tracker: Immutable<ManagedTracker>) =>
  tracker.simCard != null && tracker.simCard.status === SimCardStates.AKTIV;
